import { css } from 'aphrodite';
import { BioPagination, ModalWrapper, SharedCarousel, VideoModal, VideoTile } from 'components';
import { useScrollToTopOnMount } from 'hooks';
import useSessionVideos from 'hooks/useSessionVideos';
import { IBio, VideoData } from 'models';
import React, { FC, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { RootState } from 'redux/store';
import api from 'util/api';
import { GAEventAction, GAEventCategory, GAEventLabel, trackGoogleEvent } from 'util/google-helpers';
import { makeSlug } from 'util/helpers';
import { style } from './style';

interface InnovationZoneParams {
  innovationZoneCategory?: string;
}

interface ICategory {
  id: string;
  label: string;
}

const DEFAULT_ROUTE = '/innovation-zone';

export const InnovationZone: FC<{}> = () => {
  useScrollToTopOnMount();

  const styles = style();

  const cmsCategories = useSelector((state: RootState) => state.widget.cms.text.header.innovation_zone_categories);
  const { speaker_bio_button_text, speaker_bios_button_text } = useSelector(
    (state: RootState) => state.widget.cms.text.innovation_zone
  );
  const categories: ICategory[] = cmsCategories.map((label: string) => {
    return { id: makeSlug(label), label };
  });

  const { innovationZoneCategory = '' } = useParams<InnovationZoneParams>();
  const category = getCategory(innovationZoneCategory);

  const innovationZoneVideos = useSessionVideos(DEFAULT_ROUTE);
  const innovationZoneVideosPerCategory = getVideosPerCategory(category, innovationZoneVideos);
  const [videoModalData, setVideoModalData] = useState<VideoData>();
  const [bioModalData, setBioModalData] = useState<IBio[] | undefined>(undefined);

  function getCategory(id: string) {
    if (!id) return undefined;
    return categories.find((category) => category.id === id);
  }

  function getVideosPerCategory(category: ICategory | undefined, videos: Array<VideoData> = []) {
    const videosPerCategory: Record<string, Array<VideoData>> = {};
    if (category) {
      videosPerCategory[category.id] = videos.filter((video) => video.page.includes(category.id));
    } else {
      categories.forEach((category) => {
        videosPerCategory[category.id] = videos.filter((video) => video.page.includes(category.id));
      });
    }
    return videosPerCategory;
  }

  const getContent = (categoryId: string): JSX.Element | Array<JSX.Element | null> | null => {
    const category = getCategory(categoryId);
    if (category) return getCategorySection(category);
    else if (categories.length) return getCategorySections();
    return null;
  };

  // Sort videos by company name
  // Note: company name will be added under the speaker_titles property for CMS simplicity
  const sortInnovationZoneVideos = (videos: VideoData[]) => {
    return videos.sort((a, b) => a.speaker_titles[0].localeCompare(b.speaker_titles[0]));
  };

  const getCategorySection = (category: ICategory, id?: number): JSX.Element | null => {
    const videos = innovationZoneVideosPerCategory[category.id];
    sortInnovationZoneVideos(videos);
    if (!videos || !videos.length) return null;
    return (
      <div key={id} className={css(styles.categorySection)}>
        <h1>{category.label}</h1>
        <div className={css(styles.videoGrid)}>
          <div className={css(styles.videoTileContainer)}>{renderVideoTiles(videos)}</div>
        </div>
      </div>
    );
  };

  const getCategorySections = (): Array<JSX.Element | null> | null => {
    return categories.map((category: any, index: number) => getCategorySection(category, index));
  };

  const renderVideoTiles = (videos: Array<VideoData>) => {
    return videos.map((data: VideoData, index: number) => {
      return (
        <VideoTile
          disabled={false}
          videoObject={{ ...data }}
          onVideoClick={handleVideoClick}
          onBioClick={handleBioClick}
          copy={{ speaker_bio_button_text, speaker_bios_button_text }}
          maxColumnCount={3}
          key={index}
        />
      );
    });
  };

  const getVideosByCategory = (category: ICategory | undefined): Array<VideoData> => {
    if (!category) return [];
    return innovationZoneVideosPerCategory[category.id];
  };

  function getCategoryFromPagePath(page: string = ''): ICategory | undefined {
    if (!page) return undefined;
    return categories.find((category) => page.includes(category.id));
  }

  const handleBioClick = async (urls: string[]) => {
    if (!urls || !urls.length) return;
    const response = await api.fetchBios(urls);
    setBioModalData(response);
    trackGoogleEvent(GAEventCategory.SITE, GAEventAction.CLICK, GAEventLabel.SPEAKER_BIO);
  };

  const handleVideoClick = (video: VideoData) => {
    window.scrollTo(0, 0);
    setVideoModalData(video);
  };

  return (
    <>
      <main className={css(styles.main)}>
        <section>{getContent(innovationZoneCategory)}</section>
        <div className={css(styles.carousel)}>
          <SharedCarousel />
        </div>
      </main>
      {videoModalData && (
        <VideoModal
          active={!!videoModalData}
          video={videoModalData}
          videos={getVideosByCategory(getCategoryFromPagePath(videoModalData.page))}
          autoplay
          autoplayNext
          onRequestClose={() => setVideoModalData(undefined)}
        />
      )}
      <ModalWrapper
        isOpen={!!bioModalData}
        options={{ fillSmallerScreens: true, preventScroll: true }}
        onRequestClose={() => setBioModalData(undefined)}
        customStyles={{
          modalContent: {
            desktop: { padding: '80px 26px 30px 69px' },
            tablet: { padding: '80px 20px 56px 30px' },
            mobile: { padding: '80px 20px 40px 20px' },
          },
        }}>
        <BioPagination bios={bioModalData || []} />
      </ModalWrapper>
    </>
  );
};
